<script lang="ts" setup>
import {
  getEditorialChildLocations,
  getEditorialConfiguration,
  type GetEditorialConfigurationResponse,
  type GetEditorialContentsChildEditorialLocationResponse,
  getEditorialLocation,
  getEditorialLocationBuilders,
  getEditorialLocationSearchResults,
  type GetEditorialLocationResponse,
  type GetEditorialSearchResultsResponse,
} from '@/api';
import EditorialStatisticsSection from '@/views/editorial/EditorialStatisticsSection.vue';
import { buildStatisticsFaqItems, getLocationPhrase, getStatistics, selectStatisticsTableProjects, shouldShowStatisticsSection } from '@/views/editorial/useEditorialStatisticsSection.ts';
import { extractMarketStats, needsStats, stripUnresolvedStatBlocks, useEditorialMarketStats } from '@/views/editorial/useEditorialMarketStats.ts';
import { useHead, type ResolvableLink, type ResolvableScript } from '@unhead/vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { computed, ref, watch } from 'vue';
import SectionHeader from '@/components/SectionHeader.vue';
import EditorialSearchResults from '@/views/editorial/EditorialSearchResults.vue';
import EditorialSubscribeBanner from '@/views/editorial/EditorialSubscribeBanner.vue';
import EditorialLocationCard from '@/views/editorial/EditorialLocationCard.vue';
import { getCanonicalUrl } from '@/utilities';
import { buildHreflangLinks, buildSocialMeta } from '@/utilities/seo.ts';
import { useEditorialJsonSchema } from '@/utilities/json-ld.ts';
import { Constants } from '@/utilities/constants';
import { faAnglesDown, faChevronDown } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { getEditorialAlternateLink } from '@/plugins/router/localized.ts';
import AdUnit from '@/components/AdUnit.vue';
import EditorialRelatedGuides from '@/views/editorial/EditorialRelatedGuides.vue';
import EditorialNearbySearchResults from '@/views/editorial/EditorialNearbySearchResults.vue';
import MoreResultsButton from '@/components/MoreResultsButton.vue';
import { useSsgState } from '@/utilities/ssg-state.ts';

const props = defineProps<{
  categorySlug: string;
  locationSlug: string;
}>();

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const canonicalUrl = computed(() => getCanonicalUrl(route.path));
const { generateEditorialBreadcrumbJsonSchema, generateEditorialFaqJsonSchema, generateEditorialProjectListJsonSchema } = useEditorialJsonSchema();

const location = ref<GetEditorialLocationResponse | null>(null);
const childLocations = ref<GetEditorialContentsChildEditorialLocationResponse[]>([]);
const alternateLink = ref<{ hreflang: string; href: string } | null>(null);
const buildersCount = ref(0);

// Pilote SEO : contenu éditorial enrichi, limité à la page des maisons neuves de Laval pour l'instant.
// Le slug de localisation est identique dans les deux locales, seul celui de la catégorie change.
const pilotCategorySlugs = ['maisons-neuves-a-vendre', 'new-houses-for-sale'];
const isLavalPilot = computed(() => props.locationSlug === 'laval' && pilotCategorySlugs.includes(props.categorySlug));

const { marketStats, marketMinPrice, resolveAvailablePlaceholders, resolvePlaceholders } = useEditorialMarketStats();

const editorialConfiguration = ref<GetEditorialConfigurationResponse | null>(null);
const searchResults = ref<GetEditorialSearchResultsResponse | null>(null);

const fetchEditorialConfiguration = async () => {
  try {
    const response = await getEditorialConfiguration();
    editorialConfiguration.value = response.response?.ok && response.data ? response.data : null;
  } catch (error) {
    console.error('Error fetching editorial configuration:', error);
    editorialConfiguration.value = null;
  }
};

const fetchMarketStats = async () => {
  try {
    // La première page au format maximal nourrit à la fois l'aperçu du marché et le tableau de projets de la section statistiques
    const response = await getEditorialLocationSearchResults({
      path: {
        editorialCategorySlug: props.categorySlug,
        editorialLocationSlug: props.locationSlug,
      },
      query: { page: 1, pageSize: 45 },
    });

    if (!response.response?.ok || !response.data) {
      marketStats.value = null;
      searchResults.value = null;
      return;
    }

    marketStats.value = extractMarketStats(response.data);
    searchResults.value = response.data;
  } catch (error) {
    console.error('Error fetching editorial location stats:', error);
    marketStats.value = null;
    searchResults.value = null;
  }
};

const fetchLocation = async () => {
  try {
    const response = await getEditorialLocation({
      path: {
        editorialCategorySlug: props.categorySlug,
        editorialLocationSlug: props.locationSlug,
      },
    });

    if (response.response?.ok && response.data) {
      location.value = response.data;
    } else {
      location.value = null;
      await router.push({ name: 'not-found' });
    }
  } catch (error) {
    console.error('Error fetching location:', error);
    location.value = null;
    await router.push({ name: 'not-found' });
  }
};

const fetchChildLocations = async () => {
  try {
    const response = await getEditorialChildLocations({
      path: {
        editorialCategorySlug: props.categorySlug,
        editorialLocationSlug: props.locationSlug,
      },
    });

    if (response.response?.ok && response.data) {
      childLocations.value = response.data.items.map((item) => ({ ...item, description: '', content: '' }));
    } else {
      childLocations.value = [];
    }
  } catch (error) {
    console.error('Error fetching child locations:', error);
    childLocations.value = [];
  }
};

const loadAlternateLink = async () => {
  try {
    const link = await getEditorialAlternateLink(route);
    alternateLink.value = link ?? null;
  } catch (error) {
    console.error('Error fetching alternate link:', error);
    alternateLink.value = null;
  }
};

const fetchBuildersCount = async () => {
  try {
    const response = await getEditorialLocationBuilders({
      path: {
        editorialCategorySlug: props.categorySlug,
        editorialLocationSlug: props.locationSlug,
      },
    });

    buildersCount.value = response.response?.ok && response.data ? response.data.items.length : 0;
  } catch (error) {
    console.error('Error fetching builders count:', error);
    buildersCount.value = 0;
  }
};

const loadData = async () => {
  await fetchLocation();
  await fetchChildLocations();

  if (location.value) {
    await loadAlternateLink();
    await fetchBuildersCount();
    await fetchEditorialConfiguration();
  }

  if (location.value && (editorialConfiguration.value?.statisticsSectionEnabled || isLavalPilot.value || introNeedsStats.value)) {
    await fetchMarketStats();
  }
};

useSsgState({
  key: `editorial-location:${props.categorySlug}/${props.locationSlug}`,
  refs: { location, childLocations, alternateLink, buildersCount, editorialConfiguration, marketStats, searchResults },
  isLoaded: () => location.value !== null,
  load: loadData,
});

watch(
  () => [props.categorySlug, props.locationSlug],
  () => {
    void loadData();
  },
  { immediate: false }
);

// Identifiant d'ancre neutre, partagé par le raccourci de cette page, celui des cartes et le routeur
const childLocationsAnchor = Constants.ScrollAnchors.EditorialChildLocations;

const heroImage = computed(() => location.value?.heroImage);
const breadcrumbCategory = computed(() => location.value?.category);

// Lien retour vers la page des constructeurs du même lieu, seulement quand elle est indexable
// (même seuil que le sitemap) : sous le seuil, on n'envoie ni visiteurs ni robots vers une page presque vide.
const showBuildersLink = computed(() => buildersCount.value >= Constants.BuildersLocationMinimumCount);
// Même convention que sur la page constructeurs : le complément de lieu est le titre amputé du préfixe
// formé par le titre de la catégorie (« Projets immobiliers dans les Laurentides » devient « dans les Laurentides »).
const buildersLinkLocationPhrase = computed(() => getLocationPhrase(location.value?.title, location.value?.category.title));
const buildersLinkText = computed(() => (buildersLinkLocationPhrase.value ? t('views.editorial.location.view-builders', { locationPhrase: buildersLinkLocationPhrase.value }) : t('views.editorial.location.view-builders-generic')));
const hasChildLocations = computed(() => childLocations.value.length > 0);

// L'aperçu du marché reste dans le HTML statique mais ne s'affiche qu'au clic sur le H1
const introExpanded = ref(false);

const pilotHasIntroStats = computed(() => marketMinPrice.value !== null && marketStats.value?.count != null);

const locationIntro = computed(() => (location.value?.intro ?? '').trim());
const locationContent = computed(() => (location.value?.content ?? '').trim());
const introNeedsStats = computed(() => needsStats(locationIntro.value) || needsStats(locationContent.value));

const adminIntroParagraphs = computed(() => {
  if (!locationIntro.value) return [];

  const text = resolvePlaceholders(locationIntro.value);
  if (text === null) return [];

  return text
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter((paragraph) => paragraph.length > 0);
});

// Un Contenu qui apporte ses propres sections H2 prend le contrôle complet du bloc du bas :
// pas de titre automatique « Les avantages », et il remplace les sections codées du pilote Laval
const contentHasOwnSections = computed(() => /<h2[\s>]/i.test(locationContent.value));

// Le bloc principal reste affiché même si une statistique manque : chaque bloc dont une statistique
// reste non résolue est retiré du rendu, si bien que les accolades ne sont jamais visibles
const contentHtml = computed(() => (locationContent.value ? stripUnresolvedStatBlocks(resolveAvailablePlaceholders(locationContent.value).toHtmlContent()) : ''));

// L'intro admin a priorité ; le texte codé du pilote Laval sert de repli tant que le champ est vide
const hasIntro = computed(() => adminIntroParagraphs.value.length > 0 || isLavalPilot.value);

// Secteurs mis en valeur dans le texte, résolus depuis les villes enfants pour garder noms et liens fidèles à l'API
const pilotSectorSlugs = ['duvernay', 'sainte-dorothee', 'auteuil', 'chomedey'];

const pilotSectors = computed(() => {
  if (!isLavalPilot.value) return [];

  return pilotSectorSlugs
    .map((slug) => childLocations.value.find((childLocation) => childLocation.slug === slug))
    .filter((childLocation): childLocation is GetEditorialContentsChildEditorialLocationResponse => !!childLocation)
    .map((childLocation) => ({
      slug: childLocation.slug,
      name: childLocation.name,
      keypath: `views.editorial.location.laval.sectors.${childLocation.slug}`,
      route: {
        name: 'editorial-child-location',
        params: {
          categorySlug: props.categorySlug,
          parentLocationSlug: props.locationSlug,
          childLocationSlug: childLocation.slug,
        },
      },
    }));
});

const pilotFaqItems = computed(() => {
  if (!isLavalPilot.value || contentHasOwnSections.value) return [];

  return [
    { question: t('views.editorial.location.laval.faq.types.question'), answer: t('views.editorial.location.laval.faq.types.answer') },
    { question: t('views.editorial.location.laval.faq.warranty.question'), answer: t('views.editorial.location.laval.faq.warranty.answer') },
    { question: t('views.editorial.location.laval.faq.taxes.question'), answer: t('views.editorial.location.laval.faq.taxes.answer') },
    { question: t('views.editorial.location.laval.faq.delivery.question'), answer: t('views.editorial.location.laval.faq.delivery.answer') },
  ];
});

// Le pilote Laval garde ses sections rédigées à la main jusqu'au transfert de leur contenu dans l'admin ;
// partout ailleurs, la section statistiques automatique prend le relais
const showStatisticsSection = computed(() => {
  if (isLavalPilot.value && !contentHasOwnSections.value) return false;

  return shouldShowStatisticsSection(editorialConfiguration.value, searchResults.value);
});

const statisticsTableProjects = computed(() => (showStatisticsSection.value ? selectStatisticsTableProjects(searchResults.value, editorialConfiguration.value?.statisticsSectionProjectTableLimit ?? 0) : []));

// Un Contenu admin qui structure ses propres sections garde sa propre FAQ : la FAQ automatique s'efface
const statisticsFaqItems = computed(() => {
  if (!showStatisticsSection.value || contentHasOwnSections.value) return [];

  const statistics = getStatistics(searchResults.value);

  return statistics ? buildStatisticsFaqItems(t, buildersLinkLocationPhrase.value, statistics) : [];
});

const faqJsonLd = computed(() => {
  const items = pilotFaqItems.value.length > 0 ? pilotFaqItems.value : statisticsFaqItems.value;

  return items.length > 0 ? generateEditorialFaqJsonSchema(canonicalUrl.value, items) : null;
});

const projectListJsonLd = computed(() => (statisticsTableProjects.value.length > 0 ? generateEditorialProjectListJsonSchema(canonicalUrl.value, statisticsTableProjects.value) : null));

const breadcrumbJsonLd = computed(() => {
  if (!location.value) return null;

  return generateEditorialBreadcrumbJsonSchema(canonicalUrl.value, [
    { name: t('views.editorial.breadcrumb.home'), to: { name: 'home' } },
    { name: t('views.editorial.breadcrumb.editorial'), to: { name: 'editorial' } },
    { name: breadcrumbCategory.value?.title || props.categorySlug, to: { name: 'editorial-category', params: { categorySlug: props.categorySlug } } },
    { name: location.value.title, to: { name: 'editorial-location', params: { categorySlug: props.categorySlug, locationSlug: props.locationSlug } } },
  ]);
});

useHead({
  title: () => (location.value ? t('views.editorial.location.title', { title: location.value.title }) : ''),
  meta: () => {
    if (!location.value) return [];

    return buildSocialMeta({
      title: location.value.title,
      description: location.value.description,
      summary: location.value.summary,
      canonicalUrl: canonicalUrl.value,
      siteName: t('global.site-name'),
      image: heroImage.value,
    });
  },
  link: () => {
    const links: ResolvableLink[] = [];

    if (canonicalUrl.value) {
      links.push({
        rel: 'canonical',
        href: canonicalUrl.value,
      });
    }

    links.push(...buildHreflangLinks(canonicalUrl.value, alternateLink.value));

    return links;
  },
  script: (): ResolvableScript[] => {
    const scripts: ResolvableScript[] = [];

    if (breadcrumbJsonLd.value) {
      scripts.push({ type: 'application/ld+json', innerHTML: breadcrumbJsonLd.value });
    }

    if (faqJsonLd.value) {
      scripts.push({ type: 'application/ld+json', innerHTML: faqJsonLd.value });
    }

    if (projectListJsonLd.value) {
      scripts.push({ type: 'application/ld+json', innerHTML: projectListJsonLd.value });
    }

    return scripts;
  },
});
</script>

<template>
  <article v-if="location">
    <nav class="text-sm text-gray-500">
      <ol class="flex items-center space-x-2">
        <li>
          <RouterLink :to="{ name: 'editorial' }" class="hover:text-ph-green-600">
            {{ t('views.editorial.breadcrumb.editorial') }}
          </RouterLink>
        </li>
        <li>/</li>
        <li>
          <RouterLink :to="{ name: 'editorial-category', params: { categorySlug: categorySlug } }" class="hover:text-ph-green-600">
            {{ breadcrumbCategory?.title || categorySlug }}
          </RouterLink>
        </li>
        <li>/</li>
      </ol>
    </nav>

    <div class="space-y-4 md:space-y-8">
      <!-- Le H1 devient un déclencheur qui révèle l'intro (rédigée en admin, ou repli codé du pilote Laval), présente dans le HTML statique -->
      <div v-if="hasIntro" class="mb-8">
        <h1 class="text-base font-normal text-gray-500">
          <button :aria-expanded="introExpanded" aria-controls="editorial-location-intro" class="inline-flex cursor-pointer items-center gap-1.5 text-left" type="button" @click="introExpanded = !introExpanded">
            {{ location.title }}
            <FontAwesomeIcon :class="introExpanded ? 'rotate-180' : ''" :icon="faChevronDown" aria-hidden="true" class="text-[0.75em] transition-transform" />
          </button>
        </h1>

        <div v-show="introExpanded" id="editorial-location-intro" class="mt-2 max-w-3xl space-y-2 text-sm text-gray-600">
          <template v-if="adminIntroParagraphs.length > 0">
            <p v-for="(paragraph, index) in adminIntroParagraphs" :key="index">{{ paragraph }}</p>
          </template>
          <template v-else>
            <p>
              {{ pilotHasIntroStats ? t('views.editorial.location.laval.intro.p1-stats', { count: marketStats?.count, minPrice: marketMinPrice }) : t('views.editorial.location.laval.intro.p1') }}
            </p>
            <p>{{ t('views.editorial.location.laval.intro.p2') }}</p>
          </template>
        </div>
      </div>
      <SectionHeader v-else as="h1" class="mb-8 border-b-0 text-base! font-normal! text-gray-500">{{ location?.title }}</SectionHeader>

      <EditorialSearchResults
        enable-map
        :category-slug="categorySlug"
        :filter-category-ids="location?.filter.globalCategoryIds"
        :location-slug="locationSlug"
        :price-type-id="location?.filter.priceTypeId"
        :subscribe-category-ids="location?.filter.globalCategoryIds"
        :subscribe-city-ids="location?.filter.cityIds"
        :title="location?.title"
      >
        <!-- Raccourci vers la liste des villes, plus bas dans la page. Lien natif volontaire : le scrollBehavior du routeur ignore les ancres -->
        <template v-if="hasChildLocations" #header-actions>
          <a
            :href="`#${childLocationsAnchor}`"
            class="text-ph-green-600 hover:text-ph-green-800 focus:ring-ph-green-500 inline-flex min-h-11 items-center gap-1.5 rounded-sm text-sm font-medium whitespace-nowrap transition-colors focus:ring-2 focus:ring-offset-2 focus:outline-none sm:min-h-0"
          >
            {{ t('views.editorial.location.choose-city') }}
            <FontAwesomeIcon :icon="faAnglesDown" class="text-[0.75em]" />
          </a>
        </template>
      </EditorialSearchResults>

      <EditorialSubscribeBanner :category-ids="location?.filter.globalCategoryIds ?? []" :city-ids="location?.filter.cityIds ?? []" />

      <AdUnit adUnitLocation="Leaderboard" />

      <EditorialNearbySearchResults :category-slug="categorySlug" :location-slug="locationSlug" :price-type-id="location?.filter.priceTypeId" :title="location?.title" />

      <MoreResultsButton />

      <div v-if="hasChildLocations" class="space-y-6">
        <SectionHeader :id="childLocationsAnchor" class="scroll-mt-4">{{ t('views.editorial.location.child-locations', { title: location.title }) }}</SectionHeader>

        <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
          <EditorialLocationCard v-for="(childLocation, index) in childLocations" :key="childLocation.id" :category-slug="categorySlug" :index="index" :location="childLocation" />
        </div>
      </div>
      <div v-else class="py-12 text-center">
        <p class="text-gray-500">{{ t('views.editorial.no-locations') }}</p>
      </div>

      <EditorialRelatedGuides :editorial-category-id="location.category.id" :editorial-location-id="location.id" />

      <!-- Pilote Laval : sections éditoriales codées (prix, secteurs, avantages, FAQ), tant que le Contenu admin n'apporte pas ses propres sections -->
      <template v-if="isLavalPilot && !contentHasOwnSections">
        <section class="space-y-4">
          <SectionHeader>{{ t('views.editorial.location.laval.price.header') }}</SectionHeader>

          <div class="prose prose-lg max-w-none">
            <p>{{ marketMinPrice ? t('views.editorial.location.laval.price.p1-stats', { minPrice: marketMinPrice }) : t('views.editorial.location.laval.price.p1') }}</p>
            <p>{{ t('views.editorial.location.laval.price.p2') }}</p>
          </div>
        </section>

        <section class="space-y-4">
          <SectionHeader>{{ t('views.editorial.location.laval.sectors.header') }}</SectionHeader>

          <div class="prose prose-lg max-w-none">
            <p>{{ t('views.editorial.location.laval.sectors.intro') }}</p>
            <ul v-if="pilotSectors.length > 0">
              <li v-for="sector in pilotSectors" :key="sector.slug">
                <i18n-t :keypath="sector.keypath" scope="global" tag="span">
                  <template #link>
                    <RouterLink :to="sector.route" class="text-ph-green-600 hover:text-ph-green-800 font-medium">{{ sector.name }}</RouterLink>
                  </template>
                </i18n-t>
              </li>
            </ul>
          </div>
        </section>

        <section class="space-y-4">
          <SectionHeader>{{ t('views.editorial.location.laval.why.header') }}</SectionHeader>

          <!-- flow-root pour contenir l'image flottée avant la section suivante -->
          <div class="flex flex-col space-y-4 lg:block lg:flow-root lg:space-y-0">
            <div class="space-y-2 lg:float-right lg:ml-6 lg:w-1/2">
              <div v-if="heroImage" class="relative h-64 overflow-hidden rounded-lg md:h-96">
                <img :alt="location.title" :src="heroImage.resizedToLarge()" :srcset="heroImage.toSrcset()" sizes="(max-width: 767px) 100vw, 768px" class="h-full w-full object-cover" loading="eager" fetchpriority="high" />
              </div>

              <div v-if="location.description" class="text-sm text-gray-600">
                {{ location.description }}
              </div>
            </div>

            <div class="prose prose-lg max-w-none">
              <p>{{ t('views.editorial.location.laval.why.p1') }}</p>
              <ul>
                <li>{{ t('views.editorial.location.laval.why.warranty') }}</li>
                <li>{{ t('views.editorial.location.laval.why.location') }}</li>
                <li>{{ t('views.editorial.location.laval.why.transport') }}</li>
                <li>{{ t('views.editorial.location.laval.why.lifestyle') }}</li>
              </ul>
              <p>{{ t('views.editorial.location.laval.why.p2') }}</p>
            </div>
          </div>
        </section>

        <section class="space-y-4">
          <SectionHeader>{{ t('views.editorial.location.laval.faq.header') }}</SectionHeader>

          <div class="space-y-3">
            <details v-for="item in pilotFaqItems" :key="item.question" class="group rounded-lg border border-gray-200 bg-white p-4">
              <summary class="flex cursor-pointer list-none items-center justify-between gap-4 font-medium text-gray-900">
                {{ item.question }}
                <FontAwesomeIcon :icon="faChevronDown" aria-hidden="true" class="text-[0.75em] text-gray-400 transition-transform group-open:rotate-180" />
              </summary>
              <p class="mt-3 text-gray-600">{{ item.answer }}</p>
            </details>
          </div>
        </section>
      </template>

      <template v-else>
        <!-- Le titre automatique s'efface quand le Contenu admin structure lui-même ses sections -->
        <SectionHeader v-if="!contentHasOwnSections">{{ t('views.editorial.location.advantages', { title: location.title }) }}</SectionHeader>

        <div class="flex flex-col space-y-4 lg:flow-root lg:space-y-0">
          <div class="space-y-2 lg:float-right lg:ml-6 lg:w-1/2">
            <div v-if="heroImage" class="relative h-64 overflow-hidden rounded-lg md:h-96">
              <img :alt="location.title" :src="heroImage.resizedToLarge()" :srcset="heroImage.toSrcset()" sizes="(max-width: 767px) 100vw, 768px" class="h-full w-full object-cover" loading="eager" fetchpriority="high" />
            </div>

            <div v-if="location.description" class="text-sm text-gray-600">
              {{ location.description }}
            </div>
          </div>

          <div v-if="location.content" class="prose prose-lg max-w-none">
            <div class="markdown" v-html="contentHtml"></div>
          </div>
        </div>
      </template>

      <EditorialStatisticsSection
        v-if="showStatisticsSection && searchResults"
        :bedroom-columns-minimum-fill="editorialConfiguration?.statisticsSectionBedroomColumnsMinimumFill ?? 0"
        :faq-items="statisticsFaqItems"
        :location-phrase="buildersLinkLocationPhrase"
        :projects="statisticsTableProjects"
        :response="searchResults"
        :title="location.title"
      />

      <div v-if="showBuildersLink" class="flex justify-center">
        <RouterLink
          :to="{ name: 'builders-location', params: { categorySlug: categorySlug, locationSlug: locationSlug } }"
          class="border-ph-green-600 text-ph-green-700 hover:bg-ph-green-50 focus-visible:outline-ph-green-600 rounded-lg border-2 px-6 py-2.5 text-sm font-semibold transition-colors focus-visible:outline-2 focus-visible:outline-offset-2"
        >
          {{ buildersLinkText }}
        </RouterLink>
      </div>
    </div>
  </article>
</template>

<style lang="css" scoped></style>
